import React, { useState, useEffect } from 'react'
import {
  Card, Form, Input, Button, Tag, Divider, message, Avatar, Tabs,
  Progress, Alert, Select, DatePicker, Row, Col,
} from 'antd'
import {
  UserOutlined, MailOutlined, SafetyCertificateOutlined, EditOutlined,
  SaveOutlined, CloseOutlined, LockOutlined, IdcardOutlined, BankOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, PhoneOutlined,
  FileProtectOutlined,
} from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import { authStore } from '@/context/auth'
import { useMasterData, type Employee } from '@/context/MasterDataContext'
import { api, ApiError } from '@/services/api'

/* ── Completion helpers ────────────────────────────────────────── */
interface CompletionSection {
  key: string
  label: string
  filled: boolean
}

function calcCompletion(emp: Employee | undefined): { pct: number; sections: CompletionSection[] } {
  const sections: CompletionSection[] = [
    {
      key: 'personal',
      label: 'Personal Details',
      filled: !!(emp && (emp.personalMobile || emp.dateOfBirth || emp.bloodGroup)),
    },
    {
      key: 'statutory',
      label: 'Statutory',
      filled: !!(emp && (emp.aadhaarNumber || emp.panNumber)),
    },
    {
      key: 'bank',
      label: 'Bank Details',
      filled: !!(emp && (emp.bankName || emp.accountNumber)),
    },
  ]
  const filledCount = sections.filter(s => s.filled).length
  return { pct: Math.round((filledCount / sections.length) * 100), sections }
}

/* ── Small display row ─────────────────────────────────────────── */
const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
  icon, label, value,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{
      width: 36, height: 36, borderRadius: 8, background: '#eef3ff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#4f8ef7', fontSize: 16, flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 12, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e', marginTop: 2 }}>
        {value}
      </div>
    </div>
  </div>
)

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

/* ══ Main Component ═══════════════════════════════════════════════ */
const ProfilePage: React.FC = () => {
  const user      = authStore.getUser() ?? { name: '', email: '', role: 'employee' as const }
  const { employees, setEmployees } = useMasterData()
  const location  = useLocation()

  const empRecord = employees.find(e => (e.email || '').toLowerCase() === user.email.toLowerCase())
  const { pct, sections } = calcCompletion(empRecord)
  const isComplete = pct === 100

  const defaultTab = (location.state as any)?.tab ?? 'overview'
  const [activeTab, setActiveTab] = useState<string>(defaultTab)

  const [editing,     setEditing]     = useState(false)
  const [changingPwd, setChangingPwd] = useState(false)
  const [form]        = Form.useForm()
  const [pwdForm]     = Form.useForm()
  const [personalForm] = Form.useForm()
  const [statutoryForm] = Form.useForm()
  const [bankForm]    = Form.useForm()

  const userInitial = (user.name || 'U').charAt(0).toUpperCase()

  // Populate extended forms when employee record loads
  useEffect(() => {
    if (!empRecord) return
    personalForm.setFieldsValue({
      dateOfBirth:      empRecord.dateOfBirth ? dayjs(empRecord.dateOfBirth) : undefined,
      bloodGroup:       empRecord.bloodGroup,
      personalMobile:   empRecord.personalMobile,
      altContactNumber: empRecord.altContactNumber,
      personalAddress:  empRecord.personalAddress,
    })
    statutoryForm.setFieldsValue({
      aadhaarNumber:           empRecord.aadhaarNumber,
      panNumber:               empRecord.panNumber,
      esiNumber:               empRecord.esiNumber,
      uanNumber:               empRecord.uanNumber,
      healthInsuranceNumber:   empRecord.healthInsuranceNumber,
      healthInsuranceProvider: empRecord.healthInsuranceProvider,
      termInsuranceNumber:     empRecord.termInsuranceNumber,
      termInsuranceProvider:   empRecord.termInsuranceProvider,
    })
    bankForm.setFieldsValue({
      bankName:      empRecord.bankName,
      branchName:    empRecord.branchName,
      accountNumber: empRecord.accountNumber,
      ifscCode:      empRecord.ifscCode,
    })
  }, [empRecord]) // eslint-disable-line react-hooks/exhaustive-deps

  const patchEmployee = (patch: Partial<Employee>, successMsg: string) => {
    if (!empRecord) {
      message.warning('Your employee record has not been created yet. Contact Admin.')
      return
    }
    setEmployees(prev => prev.map(e =>
      e.email?.toLowerCase() === user.email.toLowerCase() ? { ...e, ...patch } : e
    ))
    sessionStorage.setItem(`sp_profile_done_${user.email}`, '1')
    message.success(successMsg)
  }

  const onPersonalSave = (values: any) =>
    patchEmployee({
      ...values,
      dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format('YYYY-MM-DD') : undefined,
    }, 'Personal details saved!')

  const onStatutorySave = (values: any) =>
    patchEmployee(values, 'Statutory details saved!')

  const onBankSave = (values: any) =>
    patchEmployee(values, 'Bank details saved!')

  const onNameSave = (values: { name: string }) => {
    authStore.setUser({ ...user, name: values.name })
    message.success('Name updated!')
    setEditing(false)
  }

  const [pwdLoading, setPwdLoading] = React.useState(false)

  const onPasswordChange = async (values: { current: string; newPwd: string }) => {
    if (!empRecord?.id) {
      message.error('Employee record not found — cannot change password.')
      return
    }
    setPwdLoading(true)
    try {
      await api.changePassword(empRecord.id, values.current, values.newPwd)
      message.success('Password changed successfully! Use the new password next time you log in.')
      pwdForm.resetFields()
      setChangingPwd(false)
    } catch (err) {
      message.error(
        err instanceof ApiError && err.status === 401
          ? 'Current password is incorrect.'
          : 'Could not change password. Please try again.'
      )
    } finally {
      setPwdLoading(false)
    }
  }

  const progressColor = pct < 40 ? '#ef4444' : pct < 80 ? '#f59e0b' : '#10b981'

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '32px 16px' }}>

      {/* ── Header card ─────────────────────────────── */}
      <Card
        style={{
          borderRadius: 16,
          background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6b 100%)',
          marginBottom: 24, border: 'none',
        }}
        styles={{ body: { padding: '32px 28px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <Avatar
            size={76}
            style={{
              background: 'linear-gradient(135deg, #4f8ef7, #1d4ed8)', fontSize: 30,
              fontWeight: 700, flexShrink: 0, boxShadow: '0 4px 16px rgba(79,142,247,0.4)',
            }}
          >
            {userInitial}
          </Avatar>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.25 }}>{user.name}</div>
            <div style={{ fontSize: 13, color: '#94b4e8', marginTop: 3 }}>{user.email}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Tag color={user.role === 'admin' ? 'gold' : 'blue'} style={{ fontWeight: 600, borderRadius: 6 }}>
                {user.role === 'admin' ? '⭐ Administrator' : '👤 Employee'}
              </Tag>
              {empRecord?.department && (
                <Tag color="cyan" style={{ borderRadius: 6 }}>{empRecord.department}</Tag>
              )}
              {empRecord?.region && (
                <Tag color="geekblue" style={{ borderRadius: 6 }}>{empRecord.region}</Tag>
              )}
            </div>
          </div>

          {/* Profile completion circle */}
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <Progress
              type="circle"
              percent={pct}
              size={72}
              strokeColor={progressColor}
              format={p => (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{p}%</div>
              )}
              trailColor="rgba(255,255,255,0.18)"
            />
            <div style={{ fontSize: 11, color: '#94b4e8', marginTop: 4 }}>
              {isComplete ? 'Complete ✓' : 'Profile'}
            </div>
          </div>
        </div>
      </Card>

      {/* ── Incomplete-profile alert ──────────────────── */}
      {!isComplete && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24, borderRadius: 10 }}
          message={<strong>Your profile is incomplete ({pct}%)</strong>}
          description={
            <div>
              <div style={{ marginBottom: 10, color: '#78350f', fontSize: 13 }}>
                Fill in the sections below so payroll, compliance, and emergency details are ready:
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {sections.map(s => (
                  <button
                    key={s.key}
                    onClick={() => !s.filled && setActiveTab(s.key)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: s.filled ? 'default' : 'pointer',
                      background: s.filled ? '#d1fae5' : '#fef3c7',
                      color: s.filled ? '#065f46' : '#92400e',
                      border: `1px solid ${s.filled ? '#a7f3d0' : '#fcd34d'}`,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {s.filled
                      ? <CheckCircleOutlined style={{ color: '#059669' }} />
                      : <ExclamationCircleOutlined style={{ color: '#d97706' }} />
                    }
                    {s.label}
                    {!s.filled && <span style={{ fontSize: 10 }}>→ Fill now</span>}
                  </button>
                ))}
              </div>
            </div>
          }
        />
      )}

      {/* ── Tabs ─────────────────────────────────────── */}
      <Card style={{ borderRadius: 12 }} styles={{ body: { padding: '4px 24px 28px' } }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            /* ── Tab 1: Overview ────────────────── */
            {
              key: 'overview',
              label: <span><UserOutlined /> Overview</span>,
              children: (
                <div style={{ paddingTop: 8 }}>

                  {/* Basic info */}
                  <Card
                    title={<span style={{ fontWeight: 700 }}><UserOutlined style={{ marginRight: 8, color: '#4f8ef7' }} />Account Info</span>}
                    extra={!editing && (
                      <Button icon={<EditOutlined />} onClick={() => { setEditing(true); form.setFieldsValue({ name: user.name }) }}>
                        Edit Name
                      </Button>
                    )}
                    style={{ borderRadius: 10, marginBottom: 20 }}
                  >
                    {!editing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <InfoRow icon={<UserOutlined />} label="Full Name" value={user.name} />
                        <Divider style={{ margin: '4px 0' }} />
                        <InfoRow icon={<MailOutlined />} label="Email Address" value={user.email} />
                        <Divider style={{ margin: '4px 0' }} />
                        <InfoRow
                          icon={<SafetyCertificateOutlined />}
                          label="Role"
                          value={
                            <Tag color={user.role === 'admin' ? 'gold' : 'blue'} style={{ fontWeight: 600, borderRadius: 6 }}>
                              {user.role === 'admin' ? 'Administrator' : 'Employee'}
                            </Tag>
                          }
                        />
                        {empRecord?.phone && (
                          <>
                            <Divider style={{ margin: '4px 0' }} />
                            <InfoRow icon={<PhoneOutlined />} label="Official Phone" value={empRecord.phone} />
                          </>
                        )}
                        {empRecord?.dateOfJoining && (
                          <>
                            <Divider style={{ margin: '4px 0' }} />
                            <InfoRow icon={<IdcardOutlined />} label="Date of Joining" value={empRecord.dateOfJoining} />
                          </>
                        )}
                      </div>
                    ) : (
                      <Form form={form} layout="vertical" onFinish={onNameSave}>
                        <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
                          <Input prefix={<UserOutlined />} size="large" />
                        </Form.Item>
                        <Form.Item label="Email Address">
                          <Input prefix={<MailOutlined />} value={user.email} disabled size="large" />
                        </Form.Item>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <Button icon={<CloseOutlined />} onClick={() => setEditing(false)}>Cancel</Button>
                          <Button type="primary" icon={<SaveOutlined />} htmlType="submit">Save</Button>
                        </div>
                      </Form>
                    )}
                  </Card>

                  {/* Security */}
                  <Card
                    title={<span style={{ fontWeight: 700 }}><LockOutlined style={{ marginRight: 8, color: '#4f8ef7' }} />Security</span>}
                    style={{ borderRadius: 10 }}
                  >
                    {!changingPwd ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>Password</div>
                          <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>••••••••  — click to change your login password</div>
                        </div>
                        <Button icon={<EditOutlined />} onClick={() => setChangingPwd(true)}>Change Password</Button>
                      </div>
                    ) : (
                      <Form form={pwdForm} layout="vertical" onFinish={onPasswordChange}>
                        <Form.Item name="current" label="Current Password" rules={[{ required: true }]}>
                          <Input.Password prefix={<LockOutlined />} size="large" placeholder="Current password" />
                        </Form.Item>
                        <Form.Item name="newPwd" label="New Password" rules={[{ required: true }, { min: 6 }]}>
                          <Input.Password prefix={<LockOutlined />} size="large" placeholder="New password" />
                        </Form.Item>
                        <Form.Item
                          name="confirmPwd" label="Confirm New Password"
                          dependencies={['newPwd']}
                          rules={[
                            { required: true },
                            ({ getFieldValue }) => ({
                              validator(_, v) {
                                if (!v || getFieldValue('newPwd') === v) return Promise.resolve()
                                return Promise.reject(new Error('Passwords do not match'))
                              },
                            }),
                          ]}
                        >
                          <Input.Password prefix={<LockOutlined />} size="large" placeholder="Confirm password" />
                        </Form.Item>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <Button icon={<CloseOutlined />} onClick={() => { setChangingPwd(false); pwdForm.resetFields() }}>Cancel</Button>
                          <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={pwdLoading}>Update Password</Button>
                        </div>
                      </Form>
                    )}
                  </Card>
                </div>
              ),
            },

            /* ── Tab 2: Personal ────────────────── */
            {
              key: 'personal',
              label: (
                <span>
                  <UserOutlined />
                  {' Personal '}
                  {sections.find(s => s.key === 'personal')?.filled
                    ? <CheckCircleOutlined style={{ color: '#10b981', fontSize: 11 }} />
                    : <ExclamationCircleOutlined style={{ color: '#f59e0b', fontSize: 11 }} />}
                </span>
              ),
              children: (
                <div style={{ paddingTop: 8 }}>
                  {!empRecord && (
                    <Alert
                      type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                      message="Your employee profile hasn't been created yet."
                      description="Ask your Admin to create your employee record first, then you can fill in your personal details here."
                    />
                  )}
                  <Form form={personalForm} layout="vertical" onFinish={onPersonalSave} disabled={!empRecord}>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="dateOfBirth" label="Date of Birth">
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="bloodGroup" label="Blood Group">
                          <Select
                            placeholder="Select blood group"
                            options={BLOOD_GROUPS.map(b => ({ value: b, label: b }))}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="personalMobile" label="Personal Mobile">
                          <Input prefix={<PhoneOutlined />} placeholder="Personal phone number" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="altContactNumber" label="Alternate Contact">
                          <Input prefix={<PhoneOutlined />} placeholder="Alternate phone number" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="personalAddress" label="Home Address">
                      <Input.TextArea rows={3} placeholder="Full home/residential address" />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
                        Save Personal Details
                      </Button>
                    </div>
                  </Form>
                </div>
              ),
            },

            /* ── Tab 3: Statutory ───────────────── */
            {
              key: 'statutory',
              label: (
                <span>
                  <FileProtectOutlined />
                  {' Statutory '}
                  {sections.find(s => s.key === 'statutory')?.filled
                    ? <CheckCircleOutlined style={{ color: '#10b981', fontSize: 11 }} />
                    : <ExclamationCircleOutlined style={{ color: '#f59e0b', fontSize: 11 }} />}
                </span>
              ),
              children: (
                <div style={{ paddingTop: 8 }}>
                  {!empRecord && (
                    <Alert
                      type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                      message="Employee record not created yet. Contact Admin."
                    />
                  )}
                  <Form form={statutoryForm} layout="vertical" onFinish={onStatutorySave} disabled={!empRecord}>
                    <div style={{ fontWeight: 700, color: '#8A93A6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                      ID Documents
                    </div>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="aadhaarNumber" label="Aadhaar Number"
                          rules={[{ pattern: /^\d{12}$/, message: 'Must be 12 digits' }]}>
                          <Input prefix={<IdcardOutlined />} placeholder="12-digit Aadhaar number" maxLength={12} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="panNumber" label="PAN Number"
                          rules={[{ pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/, message: 'Invalid PAN format (e.g. ABCDE1234F)' }]}>
                          <Input prefix={<IdcardOutlined />} placeholder="e.g. ABCDE1234F" maxLength={10} style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider style={{ margin: '8px 0 16px' }} />
                    <div style={{ fontWeight: 700, color: '#8A93A6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                      Employment & Insurance
                    </div>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="esiNumber" label="ESI Number">
                          <Input placeholder="ESI number" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="uanNumber" label="UAN Number">
                          <Input placeholder="Universal Account Number (PF)" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="healthInsuranceProvider" label="Health Insurance Provider">
                          <Input placeholder="e.g. Star Health" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="healthInsuranceNumber" label="Health Insurance Policy No.">
                          <Input placeholder="Policy number" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="termInsuranceProvider" label="Term Insurance Provider">
                          <Input placeholder="e.g. LIC" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="termInsuranceNumber" label="Term Insurance Policy No.">
                          <Input placeholder="Policy number" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
                        Save Statutory Details
                      </Button>
                    </div>
                  </Form>
                </div>
              ),
            },

            /* ── Tab 4: Bank Details ─────────────── */
            {
              key: 'bank',
              label: (
                <span>
                  <BankOutlined />
                  {' Bank Details '}
                  {sections.find(s => s.key === 'bank')?.filled
                    ? <CheckCircleOutlined style={{ color: '#10b981', fontSize: 11 }} />
                    : <ExclamationCircleOutlined style={{ color: '#f59e0b', fontSize: 11 }} />}
                </span>
              ),
              children: (
                <div style={{ paddingTop: 8 }}>
                  {!empRecord && (
                    <Alert
                      type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                      message="Employee record not created yet. Contact Admin."
                    />
                  )}
                  <Alert
                    type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                    message="Bank details are used for salary credit and reimbursements. Please ensure accuracy."
                  />
                  <Form form={bankForm} layout="vertical" onFinish={onBankSave} disabled={!empRecord}>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="bankName" label="Bank Name">
                          <Input prefix={<BankOutlined />} placeholder="e.g. State Bank of India" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="branchName" label="Branch Name">
                          <Input placeholder="Branch name and city" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="accountNumber" label="Account Number" rules={[{ required: true, message: 'Required for payroll' }]}>
                          <Input placeholder="Bank account number" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="ifscCode" label="IFSC Code"
                          rules={[{ pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: 'Invalid IFSC (e.g. SBIN0001234)' }]}>
                          <Input placeholder="e.g. SBIN0001234" maxLength={11} style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
                        Save Bank Details
                      </Button>
                    </div>
                  </Form>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  )
}

export default ProfilePage
